import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canAccessFeature, isCompanyAdmin } from '../utils/planAccess';
import { formatTaskPriority } from '../utils/taskLabels';
import { getUploadUrl } from '../utils/uploadUrl';
import CreateCustomerModal from './CreateCustomerModal';
import CreateLocationModal from './CreateLocationModal';
import Modal from './Modal';
import TaskArticleFields from './TaskArticleFields';
import UpgradeCta from './UpgradeCta';
import UserAssignPicker from './UserAssignPicker';
import RecurrenceWeekdayPicker from './RecurrenceWeekdayPicker';
import WorkGroupSelect from './WorkGroupSelect';

const SECTION_IDS = ['essentials', 'schedule', 'work', 'advanced'];

const defaultOpenSections = () => ({
  essentials: true,
  schedule: false,
  work: false,
  advanced: false
});

function AccordionSection({ id, title, meta, open, onToggle, hasError, children }) {
  return (
    <div className={`tasks-create-acc${open ? ' open' : ''}${hasError ? ' has-error' : ''}`}>
      <button
        type="button"
        className="tasks-create-acc-head"
        onClick={() => onToggle(id)}
        aria-expanded={open}
      >
        <span>
          <strong>
            {title}
            {hasError ? <i className="tasks-create-req-dot" aria-hidden="true" /> : null}
          </strong>
          {meta ? <div className="tasks-create-acc-meta">{meta}</div> : null}
        </span>
        <span className="tasks-create-acc-chev" aria-hidden="true">›</span>
      </button>
      <div className="tasks-create-acc-body">{children}</div>
    </div>
  );
}

function checklistItemText(item) {
  if (typeof item === 'string') {
    return item;
  }
  if (item && typeof item === 'object') {
    return item.text || '';
  }
  return String(item || '');
}

function TaskCreateModal({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  handleChange,
  locations,
  customers = [],
  articles = [],
  workGroups = [],
  canUseWorkGroups = false,
  taskPresets,
  presetChecklists,
  handleTaskPresetChange,
  handlePresetChecklistChange,
  newChecklistItem,
  setNewChecklistItem,
  handleAddChecklistItem,
  handleRemoveChecklistItem,
  user,
  assignableUsers,
  selectedAssignees,
  setSelectedAssignees,
  setBannerFile,
  bannerInputRef,
  onLocationCreated,
  onCustomerCreated
}) {
  const { t } = useTranslation(['tasks', 'common', 'articles']);
  const [showCreateLocationModal, setShowCreateLocationModal] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
  const [openSections, setOpenSections] = useState(defaultOpenSections);
  const [fieldError, setFieldError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const titleInputRef = useRef(null);
  const weekdayBlockRef = useRef(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    setOpenSections(defaultOpenSections());
    setFieldError(null);
    setShowToast(false);
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, [isOpen]);

  const focusInvalid = (sectionId, field, focusEl) => {
    setOpenSections((prev) => ({ ...prev, [sectionId]: true }));
    setFieldError(field);
    setShowToast(true);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => setShowToast(false), 4000);
    requestAnimationFrame(() => {
      focusEl?.focus?.();
      focusEl?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    });
  };

  const toggleSection = (sectionId) => {
    setOpenSections((prev) => {
      const nextOpen = !prev[sectionId];
      if (nextOpen && window.matchMedia('(max-width: 600px)').matches) {
        const next = {};
        SECTION_IDS.forEach((id) => {
          next[id] = id === sectionId;
        });
        return next;
      }
      return { ...prev, [sectionId]: nextOpen };
    });
  };

  const clearFieldError = (field) => {
    setFieldError((current) => (current === field ? null : current));
  };

  const toggleRecurrence = () => {
    clearFieldError('weekdays');
    setFormData((prev) => ({
      ...prev,
      recurrenceEnabled: !prev.recurrenceEnabled
    }));
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    const title = (formData.title || '').trim();
    if (!title) {
      focusInvalid('essentials', 'title', titleInputRef.current);
      return;
    }
    if (
      formData.recurrenceEnabled
      && (!formData.recurrenceWeekdays || formData.recurrenceWeekdays.length === 0)
      && !formData.recurrenceIntervalDays
    ) {
      focusInvalid('schedule', 'weekdays', weekdayBlockRef.current);
      return;
    }
    setFieldError(null);
    setShowToast(false);
    onSubmit(event);
  };

  const selectedLocation = formData.locationId
    ? locations.find(
      (loc) => loc.id === parseInt(formData.locationId, 10) || loc.id === formData.locationId
    )
    : null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('tasks:createTaskTitle')}
        className="tasks-create-modal"
      >
        <form className="tasks-create-form" onSubmit={handleFormSubmit} noValidate>
          <div className="tasks-create-scroll">
            <p className="tasks-create-subtitle">{t('tasks:createAccordionSubtitle')}</p>

            <AccordionSection
              id="essentials"
              title={t('tasks:createSectionEssentials')}
              meta={t('tasks:createSectionEssentialsMeta')}
              open={openSections.essentials}
              onToggle={toggleSection}
              hasError={fieldError === 'title'}
            >
              {taskPresets.length > 0 && (
                <div className="form-group">
                  <label>{t('tasks:useTaskPresetLabel')}</label>
                  <select
                    className="tasks-form-select-full"
                    onChange={handleTaskPresetChange}
                  >
                    <option value="">{t('tasks:selectTaskPreset')}</option>
                    {taskPresets.map((preset) => (
                      <option key={preset.id} value={preset.id}>
                        {preset.title
                          ? t('tasks:taskPresetOption', { name: preset.name, title: preset.title })
                          : preset.name}
                      </option>
                    ))}
                  </select>
                  <small className="tasks-form-hint">
                    {t('tasks:taskPresetHint')}
                  </small>
                </div>
              )}
              <div className={`form-group${fieldError === 'title' ? ' field-error' : ''}`}>
                <label htmlFor="create-task-title">{t('tasks:titleLabel')}</label>
                <input
                  id="create-task-title"
                  ref={titleInputRef}
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={(e) => {
                    clearFieldError('title');
                    handleChange(e);
                  }}
                  aria-invalid={fieldError === 'title'}
                />
                {fieldError === 'title' && (
                  <span className="field-error-msg">{t('tasks:createTitleRequired')}</span>
                )}
              </div>
              {isCompanyAdmin(user) && (
                <>
                  <div className="form-group">
                    <label>{t('tasks:customerLabel')}</label>
                    <div className="tasks-location-row">
                      <select
                        name="customerId"
                        value={formData.customerId || ''}
                        onChange={handleChange}
                        className="tasks-location-input"
                      >
                        <option value="">{t('tasks:noCustomer')}</option>
                        {customers.map((customer) => (
                          <option key={customer.id} value={customer.id}>
                            {customer.name}
                            {customer.organization_name ? ` (${customer.organization_name})` : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowCreateCustomerModal(true)}
                      >
                        {t('tasks:createCustomer')}
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('tasks:assignToLabel')}</label>
                    <UserAssignPicker
                      users={assignableUsers}
                      selectedUsers={selectedAssignees}
                      onChange={setSelectedAssignees}
                      placeholder={t('tasks:placeholderAssignUser')}
                    />
                  </div>
                </>
              )}
              <div className="form-group">
                <label>{t('tasks:descriptionLabel')}</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>
            </AccordionSection>

            <AccordionSection
              id="schedule"
              title={t('tasks:createSectionSchedule')}
              meta={t('tasks:createSectionScheduleMeta')}
              open={openSections.schedule}
              onToggle={toggleSection}
              hasError={fieldError === 'weekdays'}
            >
              <div className="form-row">
                <div className="form-group">
                  <label>{t('tasks:scheduledDateLabel')}</label>
                  <input
                    type="date"
                    name="scheduledFor"
                    value={formData.scheduledFor}
                    onChange={handleChange}
                  />
                  <small className="tasks-form-hint">
                    {t('tasks:scheduledDateHint')}
                  </small>
                </div>
                <div className="form-group">
                  <label>{t('tasks:estimatedDurationLabel')}</label>
                  <input
                    type="number"
                    name="estimatedDuration"
                    value={formData.estimatedDuration}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('tasks:locationLabel')}</label>
                <div className="tasks-location-row">
                  <input
                    type="text"
                    name="locationName"
                    value={formData.locationName || ''}
                    onChange={handleChange}
                    placeholder={t('tasks:placeholderLocationName')}
                    list="locations-list"
                    className="tasks-location-input"
                  />
                  <datalist id="locations-list">
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.name} />
                    ))}
                  </datalist>
                  {formData.locationName && !formData.locationId && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setNewLocationName(formData.locationName);
                        setShowCreateLocationModal(true);
                      }}
                    >
                      {t('tasks:createLocation')}
                    </button>
                  )}
                </div>
                {selectedLocation && (selectedLocation.banner_image_url || selectedLocation.address) && (
                  <div className="tasks-location-preview">
                    {selectedLocation.banner_image_url && (
                      <img
                        src={getUploadUrl(selectedLocation.banner_image_url)}
                        alt={selectedLocation.name}
                      />
                    )}
                    {selectedLocation.address && (
                      <small className="tasks-location-preview-address">
                        {selectedLocation.address}
                      </small>
                    )}
                  </div>
                )}
              </div>
              {canUseWorkGroups && (
                <div className="form-group">
                  <label>{t('tasks:workGroupLabel')}</label>
                  <WorkGroupSelect
                    workGroups={workGroups}
                    workGroupId={formData.workGroupId || ''}
                    workGroupName={formData.workGroupName || ''}
                    onChange={({ workGroupId, workGroupName }) => {
                      setFormData((prev) => ({
                        ...prev,
                        workGroupId: workGroupId || '',
                        workGroupName: workGroupName || ''
                      }));
                    }}
                  />
                </div>
              )}
              <div className="form-group">
                <div className="tasks-recurrence-row">
                  <label>{t('tasks:recurrenceToggleLabel')}</label>
                  <div
                    className={`task-toggle-switch ${formData.recurrenceEnabled ? 'active' : ''}`}
                    onClick={toggleRecurrence}
                    role="switch"
                    aria-checked={Boolean(formData.recurrenceEnabled)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleRecurrence();
                      }
                    }}
                  >
                    <div className="task-toggle-knob"></div>
                  </div>
                </div>
              </div>
              {formData.recurrenceEnabled && (
                <div
                  ref={weekdayBlockRef}
                  className={`form-group${fieldError === 'weekdays' ? ' field-error' : ''}`}
                >
                  <label>{t('tasks:recurrenceWeekdaysLabel')}</label>
                  <RecurrenceWeekdayPicker
                    idPrefix="create-recurrence-day"
                    value={formData.recurrenceWeekdays || []}
                    onChange={(recurrenceWeekdays) => {
                      clearFieldError('weekdays');
                      setFormData((prev) => ({
                        ...prev,
                        recurrenceWeekdays,
                        recurrenceIntervalDays: ''
                      }));
                    }}
                  />
                  <small className="recurrence-weekday-hint">{t('tasks:recurrenceWeekdaysHint')}</small>
                  {fieldError === 'weekdays' && (
                    <span className="field-error-msg">{t('tasks:recurrenceWeekdaysRequired')}</span>
                  )}
                </div>
              )}
            </AccordionSection>

            <AccordionSection
              id="work"
              title={t('tasks:createSectionWork')}
              meta={t('tasks:createSectionWorkMeta')}
              open={openSections.work}
              onToggle={toggleSection}
            >
              <div className="form-group">
                <label>{t('tasks:checklistLabel')}</label>
                {presetChecklists.length > 0 && (
                  <div className="tasks-preset-checklist-block">
                    <label className="tasks-preset-checklist-label">{t('tasks:usePresetChecklistLabel')}</label>
                    <select
                      className="tasks-form-select-full"
                      onChange={handlePresetChecklistChange}
                    >
                      <option value="">{t('tasks:selectPresetChecklist')}</option>
                      {presetChecklists.map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="checklist-editor">
                  {formData.checklist.map((item, index) => (
                    <div key={index} className="checklist-item">
                      <span className="checklist-item-name">{checklistItemText(item)}</span>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveChecklistItem(index)}
                      >
                        {t('tasks:removeChecklistItem')}
                      </button>
                    </div>
                  ))}
                  <div className="add-checklist-item">
                    <input
                      type="text"
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      placeholder={t('tasks:placeholderChecklistItem')}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddChecklistItem();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleAddChecklistItem}
                    >
                      {t('tasks:addChecklistItem')}
                    </button>
                  </div>
                </div>
              </div>
              <TaskArticleFields
                articles={articles}
                formData={formData}
                onChange={handleChange}
              />
            </AccordionSection>

            <AccordionSection
              id="advanced"
              title={t('tasks:createSectionAdvanced')}
              meta={t('tasks:createSectionAdvancedMeta')}
              open={openSections.advanced}
              onToggle={toggleSection}
            >
              <div className="form-group">
                <label>{t('tasks:priorityLabel')}</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                >
                  <option value="low">{formatTaskPriority('low', t)}</option>
                  <option value="medium">{formatTaskPriority('medium', t)}</option>
                  <option value="high">{formatTaskPriority('high', t)}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('tasks:bannerImageLabel')}</label>
                {canAccessFeature(user, 'taskImageUploads') ? (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setBannerFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                    ref={bannerInputRef}
                  />
                ) : (
                  <UpgradeCta as="small" />
                )}
              </div>
            </AccordionSection>
          </div>

          {showToast ? (
            <div className="tasks-create-toast" role="alert">
              {t('tasks:createValidationToast')}
            </div>
          ) : null}

          <div className="tasks-create-footer modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              {t('common:cancel')}
            </button>
            <button type="submit" className="btn btn-primary">
              {t('tasks:create')}
            </button>
          </div>
        </form>
      </Modal>

      {showCreateLocationModal && (
        <CreateLocationModal
          isOpen={showCreateLocationModal}
          initialName={newLocationName}
          onClose={() => {
            setShowCreateLocationModal(false);
            setNewLocationName('');
          }}
          onSuccess={async (newLocation) => {
            if (onLocationCreated) {
              await onLocationCreated(newLocation);
            }
            setFormData((prev) => ({ ...prev, locationId: newLocation.id }));
            setShowCreateLocationModal(false);
            setNewLocationName('');
          }}
        />
      )}

      {showCreateCustomerModal && (
        <CreateCustomerModal
          isOpen={showCreateCustomerModal}
          onClose={() => setShowCreateCustomerModal(false)}
          onSuccess={async (newCustomer) => {
            if (onCustomerCreated) {
              await onCustomerCreated(newCustomer);
            }
            setFormData((prev) => ({
              ...prev,
              customerId: newCustomer?.id ? String(newCustomer.id) : prev.customerId
            }));
            setShowCreateCustomerModal(false);
          }}
        />
      )}
    </>
  );
}

export default TaskCreateModal;
